import React, { useState, useEffect } from 'react';
import { PlusCircle, Upload, Users, Mail, Shield, Bell, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getExams, getCourses, createExam, createCourse, deleteExam, deleteCourse, updateExam, updateCourse } from '../services/api';
import AddExamForm from './AddExamForm';
import EditExamForm from './EditExamForm';
import AddCourseForm from './AddCourseForm';
import EditCourseForm from './EditCourseForm';
import ExamsTable from './ExamsTable';
import CoursesTable from './CoursesTable';
import UserManagement from './UserManagement';
import AssignmentModal from './AssignmentModal';
import EmailManagement from './EmailManagement';
import UserGroupManagement from './UserGroupManagement';
import SCORMTimingEditor from './SCORMTimingEditor';
import CertificationAdmin from './CertificationAdmin';
import CertificationNotificationAdmin from './CertificationNotificationAdmin';
import { Exam, Course, NewExam, User } from '../types';
import Logo from './Logo';

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [exams, setExams] = useState<Exam[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [showAddExamForm, setShowAddExamForm] = useState(false);
  const [showAddCourseForm, setShowAddCourseForm] = useState(false);
  const [examToEdit, setExamToEdit] = useState<Exam | null>(null);
  const [courseToEdit, setCourseToEdit] = useState<Course | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'exams' | 'courses' | 'users' | 'groups' | 'emails' | 'certifications' | 'certification-admin' | 'cert-notifications'>('exams');
  const [showEmailManagement, setShowEmailManagement] = useState(false);
  const [assignmentModal, setAssignmentModal] = useState<{
    isOpen: boolean;
    type: 'exam' | 'course';
    itemId: number;
    itemName: string;
  }>({
    isOpen: false,
    type: 'exam',
    itemId: 0,
    itemName: ''
  });

  const [scormEditor, setScormEditor] = useState<{
    isOpen: boolean;
    moduleId: number;
    moduleName: string;
  }>({
    isOpen: false,
    moduleId: 0,
    moduleName: ''
  });

  const [showUserStats, setShowUserStats] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  useEffect(() => {
    fetchExams();
    fetchCourses();
  }, []);

  const fetchExams = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getExams();
      setExams(response.data || []);
    } catch (error: any) {
      console.error('Error fetching exams:', error);
      setError('Failed to load exams. Please try again later.');
      setExams([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      setIsLoading(true);
      const response = await getCourses();
      setCourses(response.data || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
      setCourses([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddExam = async (newExam: NewExam) => {
    try {
      setError(null);
      const response = await createExam(newExam);
      const createdExam = response.data.exam;
      
      if (createdExam) {
        setExams(prevExams => [...prevExams, createdExam]);
        setShowAddExamForm(false);
      } else {
        throw new Error('No exam data received from server');
      }
    } catch (error: any) {
      console.error('Error creating exam:', error);
      setError(error.response?.data?.message || 'Failed to create exam. Please try again.');
    }
  };

  const handleEditExam = async (examId: number, updatedExam: NewExam) => {
    try {
      setError(null);
      const response = await updateExam(examId, updatedExam);
      const updatedExamData = response.data.exam;
      
      setExams(prevExams => 
        prevExams.map(exam => 
          exam.id === examId ? updatedExamData : exam
        )
      );
      setExamToEdit(null);
    } catch (error: any) {
      console.error('Error updating exam:', error);
      setError(error.response?.data?.message || 'Failed to update exam. Please try again.');
    }
  };

  const handleAddCourse = async (formData: FormData) => {
    try {
      setError(null);
      const response = await createCourse(formData);
      
      if (response.data.course) {
        setCourses(prevCourses => [...prevCourses, response.data.course]);
        setShowAddCourseForm(false);
      } else {
        throw new Error('No course data received from server');
      }
    } catch (error: any) {
      console.error('Error creating course:', error);
      setError(error.response?.data?.message || 'Failed to create course. Please try again.');
    }
  };

  const handleEditCourse = async (courseId: number, formData: FormData) => {
    try {
      setError(null);
      const response = await updateCourse(courseId, formData);
      
      if (response.data.course) {
        setCourses(prevCourses => 
          prevCourses.map(course => 
            course.id === courseId ? response.data.course : course
          )
        );
        setCourseToEdit(null);
      } else {
        throw new Error('No course data received from server');
      }
    } catch (error: any) {
      console.error('Error updating course:', error);
      setError(error.response?.data?.message || 'Failed to update course. Please try again.');
    }
  };

  const handleDeleteExam = async (examId: number) => {
    if (!window.confirm('Are you sure you want to delete this exam?')) return;

    try {
      await deleteExam(examId);
      setExams(prevExams => prevExams.filter(exam => exam.id !== examId));
    } catch (error) {
      console.error('Error deleting exam:', error);
      setError('Failed to delete exam. Please try again.');
    }
  };

  const handleDeleteCourse = async (courseId: number) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;

    try {
      await deleteCourse(courseId);
      setCourses(prevCourses => prevCourses.filter(course => course.id !== courseId));
    } catch (error) {
      console.error('Error deleting course:', error);
      setError('Failed to delete course. Please try again.');
    }
  };

  const handleAssignExam = (examId: number) => {
    const exam = exams.find(e => e.id === examId);
    if (exam) {
      setAssignmentModal({
        isOpen: true,
        type: 'exam',
        itemId: examId,
        itemName: exam.name
      });
    }
  };

  const handleAssignCourse = (courseId: number) => {
    const course = courses.find(c => c.id === courseId);
    if (course) {
      setAssignmentModal({
        isOpen: true,
        type: 'course',
        itemId: courseId,
        itemName: course.title
      });
    }
  };

  const handleEditScorm = (moduleId: number, moduleName: string) => {
    setScormEditor({
      isOpen: true,
      moduleId,
      moduleName
    });
  };

  const handleCloseScormEditor = () => {
    setScormEditor({
      isOpen: false,
      moduleId: 0,
      moduleName: ''
    });
  };

  const handleScormSave = () => {
    // Refresh courses after SCORM edit
    fetchCourses();
  };


  const handleCloseAssignmentModal = () => {
    setAssignmentModal({
      isOpen: false,
      type: 'exam',
      itemId: 0,
      itemName: ''
    });
    // Refresh data after assignment
    fetchExams();
    fetchCourses();
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'exams':
        return (
          <>
            <div className="mb-6 flex space-x-4">
              <button
                onClick={() => setShowAddExamForm(true)}
                className="bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-500 transition duration-300 flex items-center"
              >
                <PlusCircle className="mr-2" /> Add New Exam
              </button>
            </div>

            {showAddExamForm && (
              <AddExamForm
                onSubmit={handleAddExam}
                onCancel={() => setShowAddExamForm(false)}
              />
            )}

            {examToEdit && (
              <EditExamForm
                exam={examToEdit}
                onSubmit={handleEditExam}
                onCancel={() => setExamToEdit(null)}
              />
            )}

            <h2 className="text-2xl font-semibold mb-4">Exams</h2>
            {isLoading ? (
              <div className="text-center py-4">Loading exams...</div>
            ) : (
              <ExamsTable 
                exams={exams}
                onDelete={handleDeleteExam}
                onEdit={setExamToEdit}
                onAssign={handleAssignExam}
              />
            )}
          </>
        );

      case 'courses':
        return (
          <>
            <div className="mb-6">
              <button
                onClick={() => setShowAddCourseForm(true)}
                className="bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-500 transition duration-300 flex items-center"
              >
                <Upload className="mr-2" /> Upload New Course
              </button>
            </div>

            {showAddCourseForm && (
              <AddCourseForm
                onSubmit={handleAddCourse}
                onCancel={() => setShowAddCourseForm(false)}
              />
            )}

            {courseToEdit && (
              <EditCourseForm
                course={courseToEdit}
                onSubmit={handleEditCourse}
                onCancel={() => setCourseToEdit(null)}
                onEditScorm={handleEditScorm}
              />
            )}

            <h2 className="text-2xl font-semibold mb-4">Uploaded Courses</h2>
            <CoursesTable 
              courses={courses}
              onDelete={handleDeleteCourse}
              onEdit={setCourseToEdit}
              onAssign={handleAssignCourse}
            />
          </>
        );

      case 'users':
        return (
          <>
            <h2 className="text-2xl font-semibold mb-4">User Management</h2>
            <UserManagement />
          </>
        );

      case 'groups':
        return (
          <>
            <h2 className="text-2xl font-semibold mb-4">User Groups</h2>
            <UserGroupManagement />
          </>
        );

              case 'emails':
          return (
            <>
              <h2 className="text-2xl font-semibold mb-4">Email Notification Settings</h2>
              <p className="text-gray-400 mb-4">
                Configure global admin email addresses for receiving notifications.
              </p>
              <div className="mb-6">
                <button
                  onClick={() => setShowEmailManagement(true)}
                  className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-500 transition duration-300 flex items-center"
                >
                  <Mail className="mr-2" /> Manage Global Admin Emails
                </button>
              </div>
              <div className="bg-yellow-500/20 border border-yellow-500 rounded-lg p-4 text-yellow-100">
                <p className="text-sm">
                  <strong>Note:</strong> For group-specific email notifications, go to the <strong>Groups</strong> tab to configure which emails receive notifications for each user group.
                </p>
              </div>
            </>
          );
        case 'certifications':
          return (
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold mb-4">Certification Management</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div 
                  className="bg-gray-800 p-6 rounded-lg shadow cursor-pointer hover:bg-gray-700 transition-colors"
                  onClick={() => navigate('/manager/dashboard')}
                >
                  <div className="text-center">
                    <Users className="mx-auto h-12 w-12 text-purple-400 mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Team Management</h3>
                    <p className="text-gray-400 text-sm">
                      Oversee team certification compliance and manage tier progressions
                    </p>
                  </div>
                </div>
                
                <div 
                  className="bg-gray-800 p-6 rounded-lg shadow cursor-pointer hover:bg-gray-700 transition-colors"
                  onClick={() => setActiveTab('certification-admin')}
                >
                  <div className="text-center">
                    <Shield className="mx-auto h-12 w-12 text-blue-400 mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Certification Admin</h3>
                    <p className="text-gray-400 text-sm">
                      Manage certification types and tier requirements
                    </p>
                  </div>
                </div>
                
                <div 
                  className="bg-gray-800 p-6 rounded-lg shadow cursor-pointer hover:bg-gray-700 transition-colors"
                  onClick={() => setActiveTab('cert-notifications')}
                >
                  <div className="text-center">
                    <Bell className="mx-auto h-12 w-12 text-yellow-400 mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Notification Settings</h3>
                    <p className="text-gray-400 text-sm">
                      Configure email notifications for certification expirations
                    </p>
                  </div>
                </div>
                
                
                <div 
                  className="bg-gray-800 p-6 rounded-lg shadow cursor-pointer hover:bg-gray-700 transition-colors"
                  onClick={() => navigate('/admin/revenue-entry')}
                >
                  <div className="text-center">
                    <Upload className="mx-auto h-12 w-12 text-green-400 mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Revenue Entry</h3>
                    <p className="text-gray-400 text-sm">
                      Manually enter revenue and pay data for technicians
                    </p>
                  </div>
                </div>
              </div>
            </div>
          );

        case 'certification-admin':
          return <CertificationAdmin />;

        case 'cert-notifications':
          return <CertificationNotificationAdmin />;


      default:
        return null;
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <Logo className="h-12" />
        <div>
          <h1 className="text-3xl font-bold text-blue-400" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            Mascott Learning Hub
          </h1>
          <h2 className="text-xl text-gray-400 mt-2">Admin Dashboard</h2>
        </div>
      </div>
      
      {error && (
        <div className="bg-red-500 text-white p-4 rounded-lg mb-4">
          {error}
        </div>
      )}

      <div className="mb-6">
        <div className="border-b border-gray-700">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('exams')}
              className={`py-4 px-1 relative ${
                activeTab === 'exams'
                  ? 'text-blue-400 border-b-2 border-blue-400'
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              Exams
            </button>
            <button
              onClick={() => setActiveTab('courses')}
              className={`py-4 px-1 relative ${
                activeTab === 'courses'
                  ? 'text-blue-400 border-b-2 border-blue-400'
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              Courses
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`py-4 px-1 relative ${
                activeTab === 'users'
                  ? 'text-blue-400 border-b-2 border-blue-400'
                  : 'text-gray-400 hover:text-gray-300'
              } flex items-center`}
            >
              <Users className="mr-2 h-5 w-5" />
              Users
            </button>
            <button
              onClick={() => setActiveTab('groups')}
              className={`py-4 px-1 relative ${
                activeTab === 'groups'
                  ? 'text-blue-400 border-b-2 border-blue-400'
                  : 'text-gray-400 hover:text-gray-300'
              } flex items-center`}
            >
              <Users className="mr-2 h-5 w-5" />
              Groups
            </button>
            <button
              onClick={() => setActiveTab('emails')}
              className={`py-4 px-1 relative ${
                activeTab === 'emails'
                  ? 'text-blue-400 border-b-2 border-blue-400'
                  : 'text-gray-400 hover:text-gray-300'
              } flex items-center`}
            >
              <Mail className="mr-2 h-5 w-5" />
              Email Settings
            </button>
            <button
              onClick={() => setActiveTab('certifications')}
              className={`py-4 px-1 relative ${
                activeTab === 'certifications'
                  ? 'text-blue-400 border-b-2 border-blue-400'
                  : 'text-gray-400 hover:text-gray-300'
              } flex items-center`}
            >
              <Shield className="mr-2 h-5 w-5" />
              Certifications
            </button>
          </nav>
        </div>
      </div>

      {renderTabContent()}

      <AssignmentModal
        isOpen={assignmentModal.isOpen}
        onClose={handleCloseAssignmentModal}
        type={assignmentModal.type}
        itemId={assignmentModal.itemId}
        itemName={assignmentModal.itemName}
      />

      {showEmailManagement && (
        <EmailManagement onClose={() => setShowEmailManagement(false)} />
      )}

      {scormEditor.isOpen && (
        <SCORMTimingEditor
          moduleId={scormEditor.moduleId}
          moduleName={scormEditor.moduleName}
          onClose={handleCloseScormEditor}
          onSave={handleScormSave}
        />
      )}
    </div>
  );
};

export default AdminDashboard;